"use client"

import { useEffect } from "react"

import { formatPageTitle } from "@/lib/page-title"

/**
 * Set the browser-tab title from the client.
 *
 * Writes `document.title = formatPageTitle(label, brand)` in an effect and
 * re-runs whenever `label` or `brand` changes, so a dynamic page whose
 * label sharpens once React Query resolves (e.g. "Invoice" → "Invoice
 * #2026-US-AB12CD") updates automatically.
 *
 * The effect intentionally does not restore a previous title on unmount: in
 * the App Router every navigation lands on a page that sets its own title,
 * so a restore step is dead weight and can flash the stale title during the
 * transition (design §5.4).
 *
 * Args:
 *   label: The page-specific leaf label. Blank/absent renders the bare
 *     brand.
 *   brand: The trailing brand; defaults to the internal `Billdozer`.
 */
export function useDocumentTitle(label?: string | null, brand?: string): void {
  useEffect(() => {
    document.title = formatPageTitle(label, brand)
  }, [label, brand])
}
